/**
 * Shopify Forms loads in app embed(s); form markup may live in nested open shadow roots.
 * Hides floating labels when inputs are focused, filled, or autofilled.
 */
(function shopifyFormsHideLabels() {
  const HIDDEN_CLASS = 'js-forms-label-hide-hidden';
  const TEXTAREA_WRAP_CLASS = 'js-forms-textarea-wrap-has-text';
  const INIT_ATTR = 'data-forms-label-hide-init';
  const BOUND_ATTR = 'data-forms-label-hide-bound';
  const AUTOFILL_ANIM_NAME = 'shopify-forms-label-autofill-detect';

  const css = `
    label.${HIDDEN_CLASS} {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      clip: rect(0 0 0 0) !important;
      clip-path: inset(50%) !important;
      height: 1px !important;
      width: 1px !important;
      margin: -1px !important;
      overflow: hidden !important;
      white-space: nowrap !important;
      border: 0 !important;
      padding: 0 !important;
      position: absolute !important;
    }

    @keyframes ${AUTOFILL_ANIM_NAME} {
      from {
        opacity: 1;
      }
      to {
        opacity: 1;
      }
    }

    input:-webkit-autofill,
    textarea:-webkit-autofill,
    select:-webkit-autofill {
      animation-name: ${AUTOFILL_ANIM_NAME};
      animation-duration: 0.001s;
    }

    /*
     * Default Forms styles use asymmetric padding (top > bottom) for floating labels.
     * With labels hidden, equal top/bottom padding keeps the same total vertical inset
     * (1.5 + 0.5 = 2× base → 1 + 1 = 2× base) so overall control height stays the same.
     */
    form[data-testid="form"] input:not([type="hidden"]):not([type="submit"]):not([type="button"]),
    form[data-testid="form"] textarea,
    form[data-testid="form"] select {
      padding-top: calc(var(--form-field-padding, 15px) * 1) !important;
      padding-bottom: calc(var(--form-field-padding, 15px) * 1) !important;
    }

    /*
     * Single-line fields: line-height tuned with symmetric padding so text looks vertically centered.
     */
    form[data-testid="form"] input:not([type="hidden"]):not([type="submit"]):not([type="button"]),
    form[data-testid="form"] select {
      line-height: calc(var(--forms-base-font-size, 14px) * 1.45) !important;
    }

    form[data-testid="form"] textarea {
      line-height: calc(var(--forms-base-font-size, 14px) * 1.45) !important;
    }

    /*
     * Textarea wrappers may use ::before (floating label shim). Turn it off while the field
     * is focused/active, when it has content, or when JS marks the wrapper (no placeholder).
     */
    form[data-testid="form"] div:has(textarea:focus)::before,
    form[data-testid="form"] div:has(textarea:active)::before,
    form[data-testid="form"] div:has(textarea:not(:placeholder-shown))::before,
    form[data-testid="form"] div.${TEXTAREA_WRAP_CLASS}::before {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      content: none !important;
    }
  `;

  const styledRoots = new WeakSet();

  function injectStyleIntoRoot(root) {
    if (!(root instanceof ShadowRoot)) return;
    if (styledRoots.has(root)) return;
    if (root.querySelector('style[data-forms-label-hide-style]')) {
      styledRoots.add(root);
      return;
    }
    const style = document.createElement('style');
    style.setAttribute('data-forms-label-hide-style', '');
    style.textContent = css;
    root.prepend(style);
    styledRoots.add(root);
  }

  /**
   * Forms app may nest components (each with its own shadow root). Find first real <form>.
   */
  function findFormDeep(root, depth = 0) {
    if (!root || depth > 24) return null;

    const direct =
      root.querySelector('form[data-testid="form"]') ||
      root.querySelector('form[novalidate]') ||
      root.querySelector('form');

    if (direct && direct.tagName === 'FORM') {
      return direct;
    }

    const candidates = root.querySelectorAll('*');
    for (let i = 0; i < candidates.length; i++) {
      const el = candidates[i];
      if (el.shadowRoot) {
        const nested = findFormDeep(el.shadowRoot, depth + 1);
        if (nested) return nested;
      }
    }

    return null;
  }

  function labelForField(field, rootNode) {
    const aria = field.getAttribute('aria-labelledby');
    if (aria) {
      for (const rawId of aria.trim().split(/\s+/)) {
        try {
          const el = rootNode.querySelector(`#${CSS.escape(rawId)}`);
          if (el?.tagName === 'LABEL') return el;
        } catch {
          /* ignore */
        }
      }
    }
    const id = field.getAttribute('id');
    if (id) {
      try {
        const byFor = rootNode.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (byFor) return byFor;
      } catch {
        /* ignore */
      }
    }

    let container = field.parentElement;
    for (let d = 0; d < 8 && container; d++) {
      const lbl = container.querySelector(':scope > label');
      if (lbl) return lbl;
      container = container.parentElement;
    }

    return field.parentElement?.querySelector('label') ?? null;
  }

  function setLabelHidden(label, hide) {
    if (!label) return;
    label.classList.toggle(HIDDEN_CLASS, hide);

    if (hide) {
      label.style.setProperty('opacity', '0', 'important');
      label.style.setProperty('visibility', 'hidden', 'important');
      label.style.setProperty('pointer-events', 'none', 'important');
    } else {
      label.style.removeProperty('opacity');
      label.style.removeProperty('visibility');
      label.style.removeProperty('pointer-events');
    }
  }

  /**
   * Toggle wrapper class so ::before stays off when there's text but no placeholder (or
   * browsers that don't update :placeholder-shown reliably). Also targets parent + grandparent
   * because the pseudo may live one level above the textarea.
   */
  function syncTextareaWrapper(field, hidePseudo) {
    if (field.tagName !== 'TEXTAREA') return;
    const wraps = [field.parentElement, field.parentElement?.parentElement];
    for (let i = 0; i < wraps.length; i++) {
      wraps[i]?.classList.toggle(TEXTAREA_WRAP_CLASS, hidePseudo);
    }
  }

  function updateField(field) {
    const rootNode = field.getRootNode();
    if (!(rootNode instanceof ShadowRoot)) return;

    injectStyleIntoRoot(rootNode);

    let value = '';
    if (field.tagName === 'SELECT') {
      value = field.value || '';
    } else if (typeof field.value === 'string') {
      value = field.value.trim();
    }

    const filled = value.length > 0;
    const focused =
      field.matches?.(':focus') === true ||
      rootNode.activeElement === field ||
      document.activeElement === field;

    const hideFloatingChrome = filled || focused;

    if (field.tagName === 'TEXTAREA') {
      syncTextareaWrapper(field, hideFloatingChrome);
    }

    const label = labelForField(field, rootNode);
    if (!label) return;

    setLabelHidden(label, hideFloatingChrome);
  }

  function refreshAllFields(form) {
    form.querySelectorAll('input, textarea, select').forEach((field) => {
      const type = field.type;
      if (type === 'hidden' || type === 'submit' || type === 'button') return;
      updateField(field);
    });
  }

  function bindForm(form) {
    const root = form.getRootNode();
    if (root instanceof ShadowRoot) injectStyleIntoRoot(root);

    refreshAllFields(form);

    form.addEventListener(
      'focusin',
      (e) => {
        const t = e.target;
        if (t.matches?.('input, textarea, select')) updateField(t);
      },
      true
    );

    form.addEventListener(
      'focusout',
      (e) => {
        const t = e.target;
        if (!t.matches?.('input, textarea, select')) return;
        requestAnimationFrame(() => updateField(t));
      },
      true
    );

    form.addEventListener('input', (e) => {
      const t = e.target;
      if (t.matches?.('input, textarea, select')) updateField(t);
    });

    form.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches?.('input, textarea, select')) updateField(t);
    });

    form.addEventListener(
      'animationstart',
      (e) => {
        if (e.animationName !== AUTOFILL_ANIM_NAME) return;
        const t = e.target;
        if (t.matches?.('input, textarea, select')) updateField(t);
      },
      true
    );

    const onVisibilityRefresh = () => {
      if (document.visibilityState !== 'visible' || !form.isConnected) return;
      refreshAllFields(form);
    };
    document.addEventListener('visibilitychange', onVisibilityRefresh);

    const onPageShow = () => {
      if (!form.isConnected) return;
      refreshAllFields(form);
    };
    window.addEventListener('pageshow', onPageShow);

    let pollCount = 0;
    const pollId = window.setInterval(() => {
      pollCount += 1;
      if (!form.isConnected || pollCount > 48) {
        window.clearInterval(pollId);
        return;
      }
      refreshAllFields(form);
    }, 250);
  }

  function tryInitEmbed(embed, attemptsLeft) {
    if (embed.getAttribute(BOUND_ATTR) === 'true') return;

    const sr = embed.shadowRoot;

    if (!sr) {
      if (attemptsLeft <= 0) return;
      window.requestAnimationFrame(() => tryInitEmbed(embed, attemptsLeft - 1));
      return;
    }

    const form = findFormDeep(sr);
    if (form) {
      if (embed.getAttribute(BOUND_ATTR) === 'true') return;
      embed.setAttribute(BOUND_ATTR, 'true');
      bindForm(form);
      return;
    }

    if (attemptsLeft <= 0) return;

    window.requestAnimationFrame(() => tryInitEmbed(embed, attemptsLeft - 1));
  }

  /** Slow networks / late chunks: keep scanning until form mounts (~45s max). */
  function scheduleLongRetry(embed) {
    if (embed.dataset.formsLabelHideWatch === '1') return;
    embed.dataset.formsLabelHideWatch = '1';

    let n = 0;
    const max = 180;
    const id = window.setInterval(() => {
      n += 1;
      if (embed.getAttribute(BOUND_ATTR) === 'true' || !embed.isConnected || n > max) {
        window.clearInterval(id);
        return;
      }
      const sr = embed.shadowRoot;
      if (!sr) return;
      const form = findFormDeep(sr);
      if (!form) return;
      if (embed.getAttribute(BOUND_ATTR) === 'true') return;
      embed.setAttribute(BOUND_ATTR, 'true');
      bindForm(form);
      window.clearInterval(id);
    }, 250);
  }

  function scheduleInit(embed) {
    if (!(embed instanceof HTMLElement)) return;
    if (embed.getAttribute(BOUND_ATTR) === 'true') return;
    if (embed.tagName !== 'SHOPIFY-FORMS-EMBED') return;

    tryInitEmbed(embed, 900);
    scheduleLongRetry(embed);
  }

  function scanNode(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (node.matches?.('shopify-forms-embed')) {
      scheduleInit(node);
    }

    node.querySelectorAll?.('shopify-forms-embed').forEach(scheduleInit);
  }

  function bootstrap() {
    scanNode(document.documentElement);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const n of m.addedNodes) {
          scanNode(n);
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    if (window.customElements?.whenDefined) {
      window.customElements.whenDefined('shopify-forms-embed').then(() => {
        document.querySelectorAll('shopify-forms-embed').forEach(scheduleInit);
      });
    }

    window.addEventListener('load', () => {
      document.querySelectorAll('shopify-forms-embed').forEach(scheduleInit);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
